import { CrossTabOutputItem } from 'src/app/common/models/crosstab.model';

/**
 * ChartClickHelper
 *
 * Extracted from ChatbotexpandComponent. Holds the chart-rendering click handlers
 * (stack / trend / bar / grouped-bar and the VerticalChartClick dispatcher).
 *
 * Follows the existing `comp: any` helper pattern — the component keeps thin wrapper
 * methods (bound in the template) that delegate here, so all call sites remain unchanged
 * and behavior is preserved.
 */
export class ChartClickHelper {
  static onVerticalStackBarChartIconClick(comp: any, chartType: string, block: any = null, distinctColChildValues?: string[]): void {
    comp.neutralizeChartIds(block);
    const target = block || comp;
    if (target.disableStackChart) return;
    const currentOutputData = (target.OutputdataObject && target.OutputdataObject.Row?.length > 0) ? target.OutputdataObject : comp.OutputdataObject;
    if (!distinctColChildValues) {
      distinctColChildValues = comp.getDistinctColumnChildValues(currentOutputData);
    }
    comp.initializeChartData(target.StackChart);
    // Reset global color dictionary to force chart to generate new colors for isolated block
    comp.crossTabService.setColorDictionary([]);
    // Sync call with safe empty data to prevent BehaviorSubject crash in child component ngOnInit
    comp.crossTabService.StackChartData(target.StackChart, [], 'None', target.DefaultConfidenceLevel || comp.DefaultConfidenceLevel, 'percentage', 'column');

    comp.resetChartStates('stack', block);
    target.isChartView = true;
    let index = 1;

    const dataToUse = (target.CrossTabOutPutData && target.CrossTabOutPutData.length > 0) ? target.CrossTabOutPutData : comp.CrossTabOutPutData;
    dataToUse.forEach((item: CrossTabOutputItem) => {
      const shareNumber = comp.getShareNumber(item);
      const calculatedPercentage = comp.calculatePercentageValue(shareNumber, item);
      const changePy = comp.getChangeVsPY(item);

      target.StackChart.Data.push({
        'Series Name': target.isTranspose ? item.colChild.replaceAll('|', ' : ') : item.rowChild.replaceAll('|', ' : '),
        'Category Name': target.isTranspose ? item.rowChild.replaceAll('|', ' : ') : item.colChild.replaceAll('|', ' : '),
        'Rounded Value': shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        Value: shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        Label: shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        'Change value vs PY': changePy.toString(),
        Significance: comp.getSignificanceString(item),
        Significance1: comp.getSignificanceString(item), // Required by StackBarProperties
        'Sample size': comp.getSampleSizeString(item)
      });
      index++;
    });

    if (dataToUse.length > 0) {
      target.StackChart.title = dataToUse[0].colChild;
    }

    const numberType = dataToUse?.[0]?.CalculationType || 'percentage';
    const distinctRowChildValues = comp.getDistinctChildValuesFormatted(currentOutputData.Row as any);

    comp.StackChart.Properties.push({
      plotBands: false,
      lowSample: false,
      showNegative: false,
      allowOverlap: true,
      chartStyle: '',
      showLegend: true,
      gridlines: false,
      shadow: false,
      stacking: 100, // For stacked bar chart
      darkTheme: false,
      legendPosition: 'bottom',
      axisLabelOrientation: 'horizontal',
      chartTitlePosition: 'center',
      showDataLabel: true,
      datalabelStyle: (target.DefaultSampleSize || comp.DefaultSampleSize) == 'None' ? 'datalabel' : 'change',
      axisImage: false,
      dataLabelPosition: 'center',
      dataLabelColor: 'black',
      marker: 'disable',
      showRemaining: false,
      gradientBarColor: false,
      showPattern: false,
      dataLabelPositionPPT: 'Center',
      datalabelFormat: (numberType == 'mean' || dataToUse?.[0]?.rowChild?.toLowerCase().includes('mean')) ? '' : 'percentage',
      datalabelOrientation: 0,
      lineWidth: 0,
      gridLineDashStyle: '',
      markerLineWidth: 0,
      markerStyle: 'circle',
      title: '',
      toolTipStyle: 'default',
      ChartCat: distinctColChildValues,
    });

    chartType = target.isTranspose ? 'bar' : 'column';
    comp.sortChartDataBySeriesName(target.StackChart.Data, distinctRowChildValues);
    const selectedSignifcance = (target.DefaultSampleSize || comp.DefaultSampleSize) === 'Pairwise' ? 'None' : (target.DefaultSampleSize || comp.DefaultSampleSize);
    setTimeout(() => {
      comp.crossTabService.StackChartData(target.StackChart, distinctColChildValues, selectedSignifcance, target.DefaultConfidenceLevel || comp.DefaultConfidenceLevel, numberType, chartType);
    }, 100);
  }

  static onTrendChartIconClick(comp: any, block: any = null): void {
    comp.neutralizeChartIds(block);
    const target = block || comp;
    if (target.disableLineChart) return;
    target.disableLineChart = false;
    comp.initializeChartData(target.LineChart);
    // Reset global color dictionary to force chart to generate new colors for isolated block
    comp.crossTabService.setColorDictionary([]);
    // Sync call with safe empty data to prevent BehaviorSubject crash in child component ngOnInit
    comp.crossTabService.LineChartData(target.LineChart, [], 'None', comp.DefaultConfidenceLevel, 'percentage', 'column');

    comp.resetChartStates('line', block);
    target.isChartView = true;
    const currentOutputData = (target.OutputdataObject && target.OutputdataObject.Row?.length > 0) ? target.OutputdataObject : comp.OutputdataObject;
    const rowdata = currentOutputData['Row'];
    comp.updateChartDisablingFlags(block);

    let numberType: string = 'percentage';
    const distinctColChildValues = comp.getDistinctColumnChildValues(currentOutputData);
    const distinctRowChildValues = comp.getDistinctChildValuesFormatted(currentOutputData.Row as any);

    const dataToUse = (target.CrossTabOutPutData && target.CrossTabOutPutData.length > 0) ? target.CrossTabOutPutData : comp.CrossTabOutPutData;
    dataToUse.forEach((item: CrossTabOutputItem) => {
      let ShareNumber = comp.isTotalBase
        ? comp.selectedWeight == 'Unweighted'
          ? item.totalUnweightedShare
          : item.totalWeightedShare
        : comp.selectedWeight == 'Unweighted'
          ? item.answerUnweightedShare
          : item.answerWeightedShare;
      let calculatedPercentage: number | string =
        item.isDerived && item.CalculationType == 'mean'
          ? ShareNumber == null
            ? 'NA'
            : Number(ShareNumber.toFixed(1))
          : ShareNumber == null
            ? 'NA'
            : Number((ShareNumber * 100).toFixed(1));
      let changevsPY = comp.isTotalBase
        ? item.changeVsPY_Total
        : item.changeVsPY_Answer;

      let changePy: number | string =
        changevsPY == null
          ? 'NA'
          : item.isDerived && item.CalculationType == 'mean'
            ? Number(changevsPY.toFixed(1))
            : Number((changevsPY * 100).toFixed(1));

      target.LineChart.Data.push({
        'Series Name': target.isTranspose ? item.colChild.replaceAll('|', ' : ') : item.rowChild.replaceAll('|', ' : '),
        'Category Name': target.isTranspose ? item.rowChild.replaceAll('|', ' : ') : item.colChild.replaceAll('|', ' : '),
        'Rounded Value': ShareNumber == null ? 'NA' : calculatedPercentage,
        XValue: '0',
        Value: calculatedPercentage,
        Label: calculatedPercentage.toString(),
        'Change value vs PY': changePy,
        Significance: comp.isTotalBase
          ? (item.Significance_Total == null ? 0 : item.Significance_Total)
          : (item.Significance_Answer == null ? 0 : item.Significance_Answer),
        'Sample Size': comp.isTotalBase
          ? (item.totalUnweightedBase == null ? 0 : item.totalUnweightedBase)
          : (item.answerUnweightedBase == null ? 0 : item.answerUnweightedBase),
      });
    });

    if (comp.CrossTabOutPutData.length > 0) {
      target.LineChart.title = comp.CrossTabOutPutData[0].colChild;
      numberType = comp.CrossTabOutPutData[0].CalculationType;
    }

    target.LineChart.Properties.push({
      plotBands: false,
      lowSample: false,
      showNegative: false,
      allowOverlap: false,
      chartStyle: 'area',
      showLegend: true,
      gridlines: false,
      legendPosition: 'bottom',
      stacking: 0,
      darkTheme: false,
      axisLabelOrientation: 'center',
      chartTitlePosition: 'center',
      showDataLabel: true,
      datalabelFormat: (numberType == 'mean' || comp.CrossTabOutPutData?.[0]?.rowChild?.toLowerCase().includes('mean')) ? '' : 'percentage',
      dataLabelPosition: 'center',
      dataLabelColor: 'black',
      marker: 'disable',
      showRemaining: false,
      gradientBarColor: false,
      showPattern: false,
      dataLabelPositionPPT: 'center',
      datalabelStyle: comp.DefaultSampleSize == 'None' ? '' : 'sigchan',
      datalabelOrientation: 0,
      axisImage: false,
      lineWidth: 2,
      gridLineDashStyle: 'Solid',
      markerLineWidth: 1,
      markerStyle: 'circle',
      title: 'LineChart',
      shadow: false,
      toolTipStyle: 'default',
      ChartCat: distinctColChildValues,
    });

    comp.sortChartDataBySeriesName(target.LineChart.Data, distinctRowChildValues);
    let selectedSignifcance = comp.DefaultSampleSize == 'Pairwise' ? 'None' : comp.DefaultSampleSize;
    setTimeout(() => {
      comp.crossTabService.LineChartData(target.LineChart, distinctColChildValues, selectedSignifcance, comp.DefaultConfidenceLevel, numberType, '');
    }, 100);
  }

  static onVerticalBarChartIconClick(comp: any, chartType: string, block: any = null, distinctColChildValues?: string[]): void {
    comp.neutralizeChartIds(block);
    const target = block || comp;
    const currentOutputData = (target.OutputdataObject && target.OutputdataObject.Row?.length > 0) ? target.OutputdataObject : comp.OutputdataObject;
    if (!distinctColChildValues) {
      distinctColChildValues = comp.getDistinctColumnChildValues(currentOutputData);
    }
    comp.initializeChartData(target.BarChart);
    // Reset global color dictionary to force chart to generate new colors for isolated block
    comp.crossTabService.setColorDictionary([]);
    // Sync call with safe empty data to prevent BehaviorSubject crash in child component ngOnInit
    comp.crossTabService.ChartData(target.BarChart, [], 'None', target.DefaultConfidenceLevel || comp.DefaultConfidenceLevel, 'percentage', 'column');

    comp.resetChartStates('bar', block);
    let index = 1;

    const dataToUse = (target.CrossTabOutPutData && target.CrossTabOutPutData.length > 0) ? target.CrossTabOutPutData : comp.CrossTabOutPutData;
    dataToUse.forEach((item: CrossTabOutputItem) => {
      const shareNumber = comp.getShareNumber(item);
      const calculatedPercentage = comp.calculatePercentageValue(shareNumber, item);
      const changePy = comp.getChangeVsPY(item);

      target.isVerticalBarChartClick = true;
      target.isVerticalStackChartClick = false;
      target.isVerticalGroupBarChartClick = false;
      target.isTableClick = false;
      target.isLineChartClick = false;
      target.isChartView = true;

      target.BarChart.Data.push({
        'Series Name': target.isTranspose ? item.colChild.replaceAll('|', ' : ') : item.rowChild.replaceAll('|', ' : '),
        'Category Name': target.isTranspose ? item.rowChild.replaceAll('|', ' : ') : item.colChild.replaceAll('|', ' : '),
        'Rounded Value': shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        Value: shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        XValue: index.toString(),
        Label: shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        'Change value vs PY': changePy.toString(),
        Significance: comp.getSignificanceString(item),
        Significance2: comp.getSignificanceString(item),
        'Sample size': comp.getSampleSizeString(item)
      });
      index++;
    });

    if (dataToUse.length > 0) {
      target.BarChart.title = dataToUse[0].colChild;
    }

    const numberType = (dataToUse.length > 0) ? (dataToUse[0].CalculationType || 'percentage') : 'percentage';
    const distinctRowChildValues = comp.getDistinctChildValuesFormatted(currentOutputData.Row as any);

    target.BarChart.Properties.push({
      plotBands: false,
      lowSample: false,
      showNegative: false,
      allowOverlap: true,
      chartStyle: '',
      showLegend: true,
      gridlines: false,
      shadow: false,
      stacking: 0,
      darkTheme: false,
      legendPosition: 'bottom',
      axisLabelOrientation: 'horizontal',
      chartTitlePosition: 'center',
      showDataLabel: true,
      datalabelStyle: (target.DefaultSampleSize || comp.DefaultSampleSize) == 'None' ? 'datalabel' : 'change',
      axisImage: false,
      dataLabelPosition: 'center',
      dataLabelColor: 'black',
      marker: 'disable',
      showRemaining: false,
      gradientBarColor: false,
      showPattern: false,
      dataLabelPositionPPT: 'Center',
      datalabelFormat: (numberType == 'mean' || dataToUse?.[0]?.rowChild?.toLowerCase().includes('mean')) ? '' : 'percentage',
      datalabelOrientation: 0,
      lineWidth: 0,
      gridLineDashStyle: '',
      markerLineWidth: 0,
      markerStyle: 'circle',
      title: '',
      toolTipStyle: 'default',
      ChartCat: distinctColChildValues,
    });

    chartType = target.isTranspose ? 'bar' : 'column';
    comp.sortChartDataBySeriesName(target.BarChart.Data, distinctRowChildValues);
    const selectedSignifcance = (target.DefaultSampleSize || comp.DefaultSampleSize) === 'Pairwise' ? 'None' : (target.DefaultSampleSize || comp.DefaultSampleSize);
    setTimeout(() => {
      comp.crossTabService.ChartData(target.BarChart, distinctColChildValues, selectedSignifcance, target.DefaultConfidenceLevel || comp.DefaultConfidenceLevel, numberType, chartType);
    }, 100);
  }

  static onGroupVerticalChartClick(comp: any, chartType: string, block: any = null, distinctColChildValues?: string[]): void {
    comp.neutralizeChartIds(block);
    const target = block || comp;
    const currentOutputData = (target.OutputdataObject && target.OutputdataObject.Row?.length > 0) ? target.OutputdataObject : comp.OutputdataObject;
    if (!distinctColChildValues) {
      distinctColChildValues = comp.getDistinctColumnChildValues(currentOutputData);
    }
    comp.initializeChartData(target.BarChart);
    // Reset global color dictionary to force chart to generate new colors for isolated block
    comp.crossTabService.setColorDictionary([]);
    // Sync call with safe empty data to prevent BehaviorSubject crash in child component ngOnInit
    comp.crossTabService.GrpChartData(target.BarChart, [], 'None', target.DefaultConfidenceLevel || comp.DefaultConfidenceLevel, 'percentage', 'column');

    comp.resetChartStates('groupBar', block);
    target.isVerticalGroupBarChartClick = true;
    target.isVerticalBarChartClick = false;
    target.isVerticalStackChartClick = false;
    target.isTableClick = false;
    target.isLineChartClick = false;
    target.isChartView = true;

    const dataToUse = (target.CrossTabOutPutData && target.CrossTabOutPutData.length > 0) ? target.CrossTabOutPutData : comp.CrossTabOutPutData;
    dataToUse.forEach((item: CrossTabOutputItem) => {
      const shareNumber = comp.getShareNumber(item);
      const calculatedPercentage = comp.calculatePercentageValue(shareNumber, item);
      const changePy = comp.getChangeVsPY(item);

      target.BarChart.Data.push({
        'Series Name': target.isTranspose ? item.colChild.replaceAll('|', ' : ') : item.rowChild.replaceAll('|', ' : '),
        'Category Name': target.isTranspose ? item.rowChild.replaceAll('|', ' : ') : item.colChild.replaceAll('|', ' : '),
        'Rounded Value': shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        Value: shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        XValue: '1',
        Label: shareNumber === null ? 'NA' : calculatedPercentage.toString(),
        'Change value vs PY': changePy.toString(),
        Significance: comp.getSignificanceString(item),
        Significance2: comp.getSignificanceString(item),
        'Sample size': comp.getSampleSizeString(item)
      });
    });

    if (dataToUse.length > 0) {
      target.BarChart.title = dataToUse[0].colChild;
    }

    const numberType = (dataToUse.length > 0) ? (dataToUse[0].CalculationType || 'percentage') : 'percentage';
    const distinctRowChildValues = comp.getDistinctChildValuesFormatted(
      (target.isTranspose ? currentOutputData.Column : currentOutputData.Row) as any
    );

    // Only plot categories present in returned output (preview may be truncated).
    const categoriesFromData: string[] = [];
    const categoriesFromDataSet = new Set<string>();
    target.BarChart.Data.forEach((row: any) => {
      const cat = row?.['Category Name'];
      if (cat != null && cat !== '' && !categoriesFromDataSet.has(cat)) {
        categoriesFromDataSet.add(cat);
        categoriesFromData.push(cat);
      }
    });
    const selectionCats = distinctColChildValues || [];
    distinctColChildValues =
      categoriesFromData.length > 0
        ? [
          ...selectionCats.filter((c) => categoriesFromDataSet.has(c)),
          ...categoriesFromData.filter((c) => !selectionCats.includes(c)),
        ]
        : selectionCats;

    target.BarChart.Properties.push({
      plotBands: false,
      lowSample: false,
      showNegative: false,
      allowOverlap: true,
      chartStyle: '',
      showLegend: true,
      gridlines: false,
      shadow: false,
      stacking: 0,
      darkTheme: false,
      legendPosition: 'bottom',
      axisLabelOrientation: 'horizontal',
      chartTitlePosition: 'center',
      showDataLabel: true,
      datalabelStyle: (target.DefaultSampleSize || comp.DefaultSampleSize) == 'None' ? 'datalabel' : 'change',
      axisImage: false,
      dataLabelPosition: 'center',
      dataLabelColor: 'black',
      marker: 'disable',
      showRemaining: false,
      gradientBarColor: false,
      showPattern: false,
      dataLabelPositionPPT: 'Center',
      datalabelFormat: (numberType == 'mean' || dataToUse?.[0]?.rowChild?.toLowerCase().includes('mean')) ? '' : 'percentage',
      datalabelOrientation: 0,
      lineWidth: 0,
      gridLineDashStyle: '',
      markerLineWidth: 0,
      markerStyle: 'circle',
      title: '',
      toolTipStyle: 'default',
      ChartCat: distinctColChildValues,
    });

    chartType = target.isTranspose ? 'bar' : 'column';
    comp.sortChartDataBySeriesName(target.BarChart.Data, distinctRowChildValues);
    const selectedSignifcance = (target.DefaultSampleSize || comp.DefaultSampleSize) === 'Pairwise' ? 'None' : (target.DefaultSampleSize || comp.DefaultSampleSize);
    setTimeout(() => {
      comp.crossTabService.GrpChartData(target.BarChart, distinctColChildValues, selectedSignifcance, target.DefaultConfidenceLevel || comp.DefaultConfidenceLevel, numberType, chartType);
    }, 100);
  }

  static VerticalChartClick(comp: any, chartType: string, block: any = null): void {
    const target = block || comp;
    comp.variableTabData = comp.crossTabService.getVarTabData || [];
    const currentOutputData = (target.OutputdataObject && target.OutputdataObject.Row?.length > 0) ? target.OutputdataObject : comp.OutputdataObject;
    const rowdata = currentOutputData['Row'];
    const rowDataLengthValue: number = rowdata.length > 1 ? rowdata.length : rowdata[0]?.Data?.length || 0;


    comp.updateChartDisablingFlags(block);
    if (!target.isChartView) return;
    const distinctColChildValues = comp.getDistinctColumnChildValues(currentOutputData);

    comp.resetChartStates(undefined, block);
    target.isChartView = true;
    target.isLineChartClick = false;
    target.isVerticalStackChartClick = false;
    target.isVerticalBarChartClick = true;

    if (rowDataLengthValue == 1) {
      comp.onVerticalBarChartIconClick(chartType, block, distinctColChildValues);
    } else {
      comp.onGroupVerticalChartClick(chartType, block, distinctColChildValues);
    }
  }
}
